import { Component, OnDestroy, OnInit } from '@angular/core';
import { PdfTaskModalComponent } from '@components/sessions/single/pdf-task-modal/pdf-task-modal.component';
import { Session } from '@models/Sessions/Session';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthenticationService } from '@services/auth/authentication.service';
import { BusService } from '@services/bus.service';
import { EventsService } from '@services/events.service';
import {
  PdfReportService,
  PdfReportTask,
} from '@services/sessions/pdfreport.service';
import { SessionsRequestService } from '@services/sessions/sessions.request.service';
import { TrackingService } from '@services/tracking/tracking.service';

@Component({
  selector: 'app-sessions-pdf-selection-modal',
  templateUrl: './pdf-selection-modal.component.html',
  styleUrls: ['./pdf-selection-modal.component.scss'],
})
export class PdfSelectionModalComponent implements OnInit, OnDestroy {
  sessions: Session[];

  selection = [] as any;
  selectAll = false;

  constructor(
    private activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private bus: BusService,
    private events: EventsService,
    private auth: AuthenticationService,
    private pdfReport: PdfReportService,
    private sessionRequest: SessionsRequestService,
    private trackingService: TrackingService,
  ) {}

  canEnable(session: Session) {
    return !session.config.pdf_exports || session.config.pdf_exports.active;
  }

  toggleSelection(session) {
    if (!this.canEnable(session)) {
      return;
    }

    const idx = this.selection.indexOf(session);

    // Is currently selected
    if (idx > -1) {
      this.selection.splice(idx, 1);
    } else {
      // Is newly selected
      this.selection.push(session);
    }
  }

  toggleSelectAll() {
    if (this.selectAll) {
      this.selection = []; // clear, then add all
      this.sessions.map(session => {
        if (this.canEnable(session)) {
          this.selection.push(session);
        }
      });
    } else {
      this.selection = []; // clear
    }
  }

  getPdfs() {
    //
    // let's open the feedback modal, so people can see
    // the progress of the pdf creation process.
    //
    const modal = this.modalService.open(PdfTaskModalComponent, {
      size: 'lg',
      windowClass: 'big-modal',
    });

    //
    // also let's create the proper task for it.
    //
    const task = new PdfReportTask<any>(
      'Creating reports for selected topics.',
    );
    modal.componentInstance.task = task;

    task.start();
    task.onFinish.subscribe(() => {
      this.dismiss();
    });

    //
    // now since we want to do pdfs one by one,
    // we need to line them up in a queue.
    //
    const queue = [];

    this.selection.forEach(session => {
      //
      // skip sessions that cannot be exported (due to config)
      //
      if (!this.canEnable(session)) {
        return;
      }

      //
      // create the corresponding task for each session.
      //
      const sessionTask = task.add(
        new PdfReportTask<any>(`report for "${session.config.title}"`, session),
      );

      //
      // add the task and the handler function of the task
      // to the queue.
      //
      queue.push({
        task: sessionTask,
        func: () => {
          //
          // create a fetch task, for tracking the task of fetching the session's data.
          //
          const fetchTask = sessionTask
            .add(
              new PdfReportTask<any>(
                `fetching report info for "${session.config.title}"`,
              ),
            )
            .start();

          //
          // get the data.
          //
          this.sessionRequest.getReport(session._id).subscribe(data => {
            //
            // data fetched, mark the task as finished.
            //
            fetchTask.finish();

            //
            // add the pdf creation task to subtasks of the task corresponding
            // to current session.
            //
            sessionTask
              .add(this.pdfReport.open(data))
              .onFinish.subscribe(() => {
                //
                // everything is done.
                //
                sessionTask.finish();
                task.finish();
              });
          });
        },
      });

      //
      // also publish for tracking.
      //
      this.bus.publish(
        this.events.requested.action.tracking.track,
        session,
        'nav',
        'open pdf central',
      );
    });

    //
    // now lets run the handler functions in the queue one by one.
    // we will create a step function, which when done will call upon
    // itself to tend to the next step, if there is any next step.
    //
    const step = index => {
      //
      // end of the queue, nothing to do.
      //
      if (index > queue.length - 1) {
        return;
      }

      //
      // get the current item in the queue
      //
      const current = queue[index];

      //
      // mark its task as started.
      //
      current.task.start();

      //
      // call the step function for the next item
      // in the queue when the task is finished.
      //
      current.task.onFinish.subscribe(() => step(index + 1));

      //
      // run the handler function.
      //
      current.func();
    };

    //
    // and start processing the queue by starting from the first item.
    //
    step(0);
  }

  dismiss() {
    this.activeModal.dismiss();
  }

  receiveReport() {
    //    this.pdfReport.open(reportData);
  }

  reportError(error) {
    console.error(error);
  }

  receiveSessions(sessions: Session[]) {
    this.sessions = sessions
      .filter(s => this.auth.isAdmin || !s.config.restricted)
      .filter(
        s =>
          this.auth.isAdmin ||
          s.me.role <= s.config.visibility ||
          !s.config.visibility,
      );
  }

  sessionsError(error) {
    console.error(error);
  }

  ngOnInit() {
    this.bus.subscribe(
      this.events.received.data.session.all.success,
      this.receiveSessions,
      this,
    );
    this.bus.subscribe(
      this.events.received.data.session.all.failure,
      this.sessionsError,
      this,
    );
    this.bus.subscribe(
      this.events.received.data.session.report.success,
      this.receiveReport,
      this,
    );
    this.bus.subscribe(
      this.events.received.data.session.report.failure,
      this.reportError,
      this,
    );
    this.bus.publish(this.events.requested.data.session.all);
  }

  ngOnDestroy() {
    this.bus.unsubscribe(
      this.events.received.data.session.all.success,
      this.receiveSessions,
    );
    this.bus.unsubscribe(
      this.events.received.data.session.all.failure,
      this.sessionsError,
    );
    this.bus.unsubscribe(
      this.events.received.data.session.report.success,
      this.receiveReport,
    );
    this.bus.unsubscribe(
      this.events.received.data.session.report.failure,
      this.reportError,
    );
  }
}
